/**
 * Session-loss detection for #501.
 *
 * When an authenticated session dies mid-use, every authenticated API call
 * returns a code-less 401 `Authentication required`. `isSessionDeath401` is the
 * predicate that tells that apart from the benign 401s that must NOT bounce a
 * logged-in user:
 *   - `org_email_required` — a logged-in user must pick an identity (handled
 *     elsewhere by IdentityRequiredDialog).
 *   - `invalid_share_session` / `share_unavailable` — share-view only.
 * Share-view requests are excluded outright via `shareActive`, and the auth /
 * public probe endpoints are excluded by path.
 *
 * `loginPathWithNext` builds the redirect target, routing the return path
 * through `safeNext` (the open-redirect guard) so it is safe to hand to
 * `<Navigate to=…>`.
 *
 * This lives in `apps/web` — NOT in `@papan/api-client`, which is deliberately
 * ignorant of product codes.
 */
import type { ApiFailure } from '@papan/api-client'
import { safeNext } from './safe-next'

export const BENIGN_401_CODES: ReadonlySet<string> = new Set([
  'org_email_required',
  'invalid_share_session',
  'share_unavailable',
])

/**
 * True only for a 401 that means "the session is gone", never for a benign one.
 * `shareActive` is `getShareSessionToken() != null` at the call site.
 */
export function isSessionDeath401(
  failure: Pick<ApiFailure, 'status' | 'code' | 'path'>,
  ctx: { shareActive: boolean },
): boolean {
  if (failure.status !== 401) return false
  if (ctx.shareActive) return false
  if (failure.path.startsWith('/api/auth/') || failure.path.startsWith('/api/public/')) return false
  if (failure.code && BENIGN_401_CODES.has(failure.code)) return false
  return true
}

/**
 * #1316 — a 403 `tenant_access_denied` (the drive's access-denied screen keys on it, `tenant-denied.ts`): the signed-in
 * account holds no membership in the active tenant. Besides a real refusal, it is what an old account's tab gets once
 * another tab signed a DIFFERENT account in (the new cookie is valid, so no 401) — the caller re-checks who is signed
 * in. A refusal of a request pinned to an earlier identity (`staleIdentity`, D134) says nothing about this session.
 */
export function isTenantDenied403(failure: Pick<ApiFailure, 'status' | 'code' | 'staleIdentity'>): boolean {
  return failure.status === 403 && failure.code === 'tenant_access_denied' && !failure.staleIdentity
}

/** `/login?next=<sanitized current path>` — safe to hand to `<Navigate to=…>`. */
export function loginPathWithNext(currentPathWithQuery: string): string {
  const next = safeNext(currentPathWithQuery)
  return `/login?next=${encodeURIComponent(next)}`
}
